import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type EmployeeResponse,
  formatMoney,
  suggestedTfrPeriod,
  type TfrCoverageRequest,
  type TfrPaymentResponse,
  type TfrPaymentsResponse,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { peopleKeys } from './people-ui.js';
import { Link } from 'react-router';
import { reconcileWindowPath } from '../treasury/ReconcilePanel.js';

/**
 * The TFR paid to the pension fund of an employee (13/10/2026, 1.68): the
 * payments linked from the bank movements in Tesoreria › Banche, each with the
 * period it covers, from one day to another.
 */
export function EmployeeTfr({ employee }: { employee: EmployeeResponse }) {
  const canWrite = useCan('persone.write');
  const canTreasury = useCan('tesoreria.read');
  const [editing, setEditing] = useState<TfrPaymentResponse | null>(null);
  const tfr = useQuery({
    queryKey: peopleKeys.tfr(employee.id),
    queryFn: () => apiRequest<TfrPaymentsResponse>(`/people/employees/${employee.id}/tfr`),
  });
  const data = tfr.data;

  return (
    <div className="stack">
      <p className="muted small">
        TFR:{' '}
        {employee.tfr === 'fondo'
          ? `fondo pensione ${employee.pensionFundName ?? ''}`
          : 'in azienda'}
        {employee.tfr === 'fondo' &&
          employee.fundJoinedOn &&
          `, adesione dal ${formatDate(employee.fundJoinedOn)}`}
        . I versamenti si collegano dal movimento bancario, in Tesoreria › Banche con il motivo
        «TFR».
      </p>
      {tfr.isError && <p className="error">{tfr.error.message}</p>}
      {data && data.payments.length === 0 && (
        <p className="muted">Nessun versamento del TFR collegato.</p>
      )}
      {data && data.payments.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th className="numeric">Importo</th>
                  <th>Periodo coperto</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>
                      {canTreasury && payment.accountCode ? (
                        <Link
                          to={reconcileWindowPath(payment.accountCode, payment.movementId)}
                          target="_blank"
                          title="Apri il movimento bancario"
                        >
                          {formatDate(payment.date)}
                        </Link>
                      ) : (
                        formatDate(payment.date)
                      )}
                    </td>
                    <td>{payment.accountCode ?? '—'}</td>
                    <td className="numeric">{formatMoney(payment.amount)}</td>
                    <td>
                      {payment.coveredFrom && payment.coveredTo ? (
                        `${formatDate(payment.coveredFrom)} – ${formatDate(payment.coveredTo)}`
                      ) : (
                        <span className="pill warn">da indicare</span>
                      )}
                    </td>
                    {canWrite && (
                      <td>
                        <button type="button" className="link" onClick={() => setEditing(payment)}>
                          {payment.coveredFrom ? 'Modifica' : 'Indica'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small">
            {data.coveredTo ? (
              <>
                Coperto fino al <strong>{formatDate(data.coveredTo)}</strong>
              </>
            ) : (
              'Nessun periodo indicato'
            )}
            {data.uncovered > 0 &&
              ` · ${data.uncovered === 1 ? '1 versamento' : `${data.uncovered} versamenti`} senza periodo`}
            .
          </p>
          {data.gaps.map((gap) => (
            <p key={gap.from} className="warn small">
              Nessun versamento copre dal {formatDate(gap.from)} al {formatDate(gap.to)}.
            </p>
          ))}
        </>
      )}
      {editing && data && (
        <CoverageDialog
          employeeId={employee.id}
          payment={editing}
          coveredTo={data.coveredTo}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CoverageDialog({
  employeeId,
  payment,
  coveredTo,
  onClose,
}: {
  employeeId: string;
  payment: TfrPaymentResponse;
  /** The last day covered by all the payments, for the period proposed. */
  coveredTo: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const suggested = suggestedTfrPeriod(coveredTo, payment.date);
  const [from, setFrom] = useState(payment.coveredFrom ?? suggested.from);
  const [to, setTo] = useState(payment.coveredTo ?? suggested.to);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (body: TfrCoverageRequest) =>
      apiRequest(`/people/employees/${employeeId}/tfr/${payment.id}`, { method: 'PUT', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onClose();
    },
  });
  const valid = Boolean(from && to && to >= from);
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>
          Periodo coperto · versamento del {formatDate(payment.date)} ({formatMoney(payment.amount)}
          )
        </h2>
        <div className="form-grid">
          <Field label="Dal">
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field
            label="Al"
            error={from && to && to < from ? 'La fine viene dopo l’inizio' : undefined}
          >
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
        </div>
        {!payment.coveredFrom && (
          <p className="muted small">
            Proposto:{' '}
            {coveredTo
              ? 'dal giorno dopo l’ultimo periodo coperto'
              : 'il trimestre prima del versamento'}
            , fino a fine trimestre.
          </p>
        )}
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !valid}
            onClick={() => save.mutate({ coveredFrom: from, coveredTo: to })}
          >
            Salva
          </button>
        </div>
      </div>
    </dialog>
  );
}
